import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { formatPrice } from '../../utils/priceFormatter';
import BottomNav from '../../components/layouts/BottomNav';
import { FaCheckCircle } from 'react-icons/fa';

const OrderConfirmationPage = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [orderDetails, setOrderDetails] = useState(location.state?.orderDetails || null);
  const [isLoading, setIsLoading] = useState(!orderDetails);

  useEffect(() => {
    if (!orderDetails) {
      // Fetch order details if not passed via navigation
      fetchOrderDetails();
    }
  }, [orderDetails]);

  const fetchOrderDetails = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/orders/number/${orderNumber}`);
      if (response.ok) {
        const data = await response.json();
        setOrderDetails(data);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ background: "#fff", minHeight: "100vh", paddingBottom: 74 }}>
        <div style={{ textAlign: "center", padding: 44 }}>
          <div style={{
            width: 40,
            height: 40,
            border: "3px solid #f3f3f3",
            borderTop: "3px solid #333",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 20px"
          }}></div>
          Loading order details...
        </div>
        <BottomNav />
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div style={{ background: "#fff", minHeight: "100vh", paddingBottom: 74 }}>
        <div style={{ textAlign: "center", padding: 44, color: "#888" }}>
          Order not found.
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", minHeight: "100vh", paddingBottom: 74 }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", padding: "18px 18px 10px 18px",
        borderBottom: "1px solid #f3f3f3", background: "#fff", position: "sticky", top: 0, zIndex: 10
      }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            background: "none",
            border: "none",
            fontSize: 26,
            color: "#222",
            cursor: "pointer",
            lineHeight: 1,
            marginRight: 14
          }}
          aria-label="Back"
        >
          &larr;
        </button>
        <span style={{
          fontWeight: 700,
          fontSize: 22,
          color: "#232323"
        }}>
          Order Confirmation
        </span>
      </div>

      {/* Success Animation */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "40px 20px 20px",
        textAlign: "center"
      }}>
        {/* Animated Success Icon */}
        <div style={{
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "#10b981",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 24,
          animation: "successPulse 2s ease-in-out",
          position: "relative"
        }}>
          {/* Ripple Effect */}
          <div style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            border: "2px solid #10b981",
            animation: "ripple 1.5s ease-out infinite"
          }}></div>
          
          <FaCheckCircle 
            size={60}
            color="#ffffff"
            style={{
              animation: "checkmark 0.8s ease-in-out 0.3s both"
            }}
          />
        </div>

        <h1 style={{
          fontSize: 28,
          fontWeight: 700,
          color: "#333",
          marginBottom: 8
        }}>
          Order Placed Successfully!
        </h1>

        <p style={{
          fontSize: 16,
          color: "#666",
          marginBottom: 32,
          lineHeight: 1.5
        }}>
          Thank you for your order. We'll send you an email confirmation shortly.
        </p>

        {/* Order Number */}
        <div style={{
          background: "#f8f9fa",
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
          border: "1px solid #e9ecef",
          width: "100%",
          maxWidth: 400
        }}>
          <div style={{
            fontSize: 14,
            color: "#666",
            marginBottom: 8
          }}>
            Order Number
          </div>
          <div style={{
            fontSize: 20,
            fontWeight: 700,
            color: "#333",
            fontFamily: "monospace"
          }}>
            #{orderDetails.order_number}
          </div>
        </div>
      </div>

      {/* Order Details */}
      <div style={{ padding: "0 20px 20px" }}>
        {/* Customer Details */}
        <div style={{
          background: "#f8f9fa",
          borderRadius: 12,
          padding: 20,
          marginBottom: 16,
          border: "1px solid #e9ecef"
        }}>
          <h3 style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#333",
            marginBottom: 16
          }}>
            Customer Details
          </h3>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Name</div>
              <div style={{ fontSize: 14, color: "#333", fontWeight: 500 }}>
                {orderDetails.notes ? orderDetails.notes.replace('Customer: ', '') : 'N/A'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Email</div>
              <div style={{ fontSize: 14, color: "#333", fontWeight: 500 }}>
                {orderDetails.email || 'N/A'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Phone</div>
              <div style={{ fontSize: 14, color: "#333", fontWeight: 500 }}>
                {orderDetails.phone || 'N/A'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Payment</div>
              <div style={{ fontSize: 14, color: "#333", fontWeight: 500, textTransform: "uppercase" }}>
                {orderDetails.payment_method || 'N/A'}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Address</div>
            <div style={{ fontSize: 14, color: "#333", lineHeight: 1.4 }}>
              {orderDetails.shipping_address || 'N/A'}
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div style={{
          background: "#f8f9fa",
          borderRadius: 12,
          padding: 20,
          marginBottom: 16,
          border: "1px solid #e9ecef"
        }}>
          <h3 style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#333",
            marginBottom: 16
          }}>
            Order Items
          </h3>
          
          {orderDetails.items?.map((item, index) => (
            <div key={index} style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0",
              borderBottom: index < orderDetails.items.length - 1 ? "1px solid #e9ecef" : "none"
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#333"
                }}>
                  {item.product_name}
                </div>
                <div style={{
                  fontSize: 12,
                  color: "#666"
                }}>
                  Qty: {item.quantity} × {formatPrice(item.unit_price)}
                </div>
              </div>
              <div style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#333"
              }}>
                {formatPrice(item.total_price)}
              </div>
            </div>
          ))}
          
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 0",
            borderTop: "2px solid #333",
            marginTop: 12
          }}>
            <span style={{
              fontSize: 16,
              fontWeight: 600,
              color: "#333"
            }}>
              Total
            </span>
            <span style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#333"
            }}>
              {formatPrice(orderDetails.total_amount)}
            </span>
          </div>
        </div>

        {/* Order Status */}
        <div style={{
          background: "#f8f9fa",
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
          border: "1px solid #e9ecef"
        }}>
          <h3 style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#333",
            marginBottom: 16
          }}>
            Order Status
          </h3>
          
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 12
          }}>
            <div style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#10b981"
            }}></div>
            <span style={{
              fontSize: 14,
              fontWeight: 500,
              color: "#333",
              textTransform: "capitalize"
            }}>
              {orderDetails.status || 'pending'}
            </span>
          </div>
          
          <div style={{
            fontSize: 12,
            color: "#666",
            marginTop: 8
          }}>
            Order placed on {new Date(orderDetails.createdAt || Date.now()).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={() => navigate('/products')}
            style={{
              flex: 1,
              padding: "14px 20px",
              background: "#ffffff",
              color: "#333333",
              border: "1px solid #333333",
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#333333";
              e.target.style.color = "#ffffff";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#ffffff";
              e.target.style.color = "#333333";
            }}
          >
            Continue Shopping
          </button>
          
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              flex: 1,
              padding: "14px 20px",
              background: "#333333",
              color: "#ffffff",
              border: "none",
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
              transition: "background-color 0.2s ease"
            }}
            onMouseEnter={(e) => e.target.style.background = "#555555"}
            onMouseLeave={(e) => e.target.style.background = "#333333"}
          >
            Go to Dashboard
          </button>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes successPulse {
          0% { transform: scale(0.8); opacity: 0; }
          50% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes checkmark {
          0% { transform: scale(0); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes ripple {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <BottomNav />
    </div>
  );
};

export default OrderConfirmationPage;
